import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';

// A request we cancelled ourselves (axios cancel token / AbortController), e.g. on
// component unmount or when superseded by a newer request. Always benign — the
// caller intentionally walked away from the result.
export const isCanceled = (err: unknown): boolean => isAxiosError(err) && err.code === 'ERR_CANCELED';

// The browser aborted an in-flight XHR out from under us: the user navigated away,
// reloaded, backgrounded the tab (common on iOS Safari), or hit a network
// transition. Surfaces as code ECONNABORTED with message exactly "Request aborted".
// Note: request timeouts ALSO use ECONNABORTED (message "timeout of Nms..."), so we
// match the abort message specifically and leave timeouts to be reported.
export const isNetworkAbort = (err: unknown): boolean =>
  isAxiosError(err) && err.code === 'ECONNABORTED' && err.message === 'Request aborted';

// The request never reached the server, or its response never came back: the device is
// offline, DNS failed, the connection dropped mid-flight, or a proxy/CORS preflight
// blocked it. axios reports these as ERR_NETWORK with no `response` at all — distinct
// from an abort (something walked away from a live request) and from an HTTP error (the
// server did answer). Recoverable by retrying, and nothing the app can fix.
export const isNetworkFailure = (err: unknown): boolean => isAxiosError(err) && err.code === 'ERR_NETWORK';

// The request was given up on before a response arrived. ECONNABORTED covers axios'
// own timeout ("timeout of Nms exceeded"); ETIMEDOUT comes from the OS/network stack.
const isRequestTimeout = (err: unknown): boolean =>
  isAxiosError(err) &&
  (err.code === 'ETIMEDOUT' || (err.code === 'ECONNABORTED' && err.message.startsWith('timeout of')));

// Worth trying again: the request never completed for transport reasons, so an identical
// retry may well succeed. Deliberately excludes HTTP errors — the server did answer, and
// replaying the same request won't change its mind — and our own cancellations.
export const isRetriableRequestFailure = (err: unknown): boolean =>
  isNetworkFailure(err) || isNetworkAbort(err) || isRequestTimeout(err);

// Aborted/canceled requests are almost always benign and add noise without
// indicating a real problem, so they're filtered out of error reporting.
const isBenignAbort = (err: unknown): boolean => isCanceled(err) || isNetworkAbort(err);

interface ReportContext {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

let initialized = false;

// Coarse buckets, not raw numbers: a tag is only useful if you can group by it, and
// "does this only happen above 50MB" is answerable while "size was 37,441,092" is not.
export const fileSizeBucket = (bytes: number): string => {
  const mb = bytes / 1_000_000;
  if (mb < 1) return '<1MB';
  if (mb < 10) return '1-10MB';
  if (mb < 50) return '10-50MB';
  return '50MB+';
};

export const pageCountBucket = (pages: number): string => {
  if (pages <= 10) return '1-10';
  if (pages <= 50) return '11-50';
  if (pages <= 200) return '51-200';
  return '200+';
};

/** How the document in the editor got there — one of a handful of values, so groupable. */
export type DocumentSource = 'new-upload' | 'saved' | 'results';

// The Network Information API isn't in TypeScript's DOM lib and isn't universal
// (notably absent on Safari/iOS), so it's modelled here as optional. It deliberately
// does NOT extend EventTarget: some Android browsers ship the pre-EventTarget shape
// with only an `onchange` property — see subscribeToConnectionChange.
interface NetworkInformationLike {
  effectiveType?: string;
  addEventListener?: EventTarget['addEventListener'];
  onchange?: (() => void) | null;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformationLike;
}

// Guest id identifies everyone, signed in or not, and is the same value the app sends as
// the `x-guest-id` header — so it joins straight onto File.guest_id / UserLog.guest_id
// server-side. The account id rides along as `username`; email is deliberately never
// sent, to keep customer addresses out of a third-party system.
let guestId: string | undefined;
let accountId: string | undefined;

const applyIdentity = (): void => {
  if (!initialized) return;
  Sentry.setUser({ id: guestId, username: accountId });
};

/**
 * The Network Information API became an EventTarget only in its second revision, and not
 * every browser followed: on some Android builds `navigator.connection` carries
 * `effectiveType` but no `addEventListener`, exposing just the original `onchange` property.
 * Feature-detect rather than trusting the object's shape.
 */
const subscribeToConnectionChange = (connection: NetworkInformationLike, update: () => void): void => {
  if (typeof connection.addEventListener === 'function') {
    connection.addEventListener('change', update);
  } else if ('onchange' in connection) {
    connection.onchange = update;
  }
};

/**
 * Mirrors the browser's connectivity onto every event. Set as tags rather than read at
 * report time so they're present on errors Sentry captures through its global handlers.
 */
const trackConnection = (): void => {
  const connection = (navigator as NavigatorWithConnection).connection;

  const update = () => {
    Sentry.setTag('online', navigator.onLine ? 'yes' : 'no');
    if (connection?.effectiveType) {
      Sentry.setTag('connectionType', connection.effectiveType);
    }
  };

  update();
  window.addEventListener('online', update);
  window.addEventListener('offline', update);
  if (connection) {
    subscribeToConnectionChange(connection, update);
  }
};

type BreadcrumbLevel = 'debug' | 'info' | 'warning' | 'error';

interface Breadcrumb {
  category: string;
  message: string;
  level?: BreadcrumbLevel;
  data?: Record<string, unknown>;
}

const SENTRY_ENVIRONMENT: string = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE;

// Noise from scripts that aren't ours. `allowUrls` below can't catch these: an extension
// or an inline third-party snippet (Microsoft Clarity bootstraps inline) runs in the
// document's context, so its frames are attributed to editar-pdf.com and pass the
// filter. Matching on the message is the only lever left.
//
// Every entry here was verified against a real issue and confirmed absent from our
// source — this list must never grow by copy-paste, or it will start hiding our bugs.
const THIRD_PARTY_NOISE = [
  // Injected script redeclaring its own global; `nativeIframe` appears
  // nowhere in this repo.
  /Identifier 'nativeIframe' has already been declared/,
  // Microsoft Clarity internals; we have no `.sequence` anywhere.
  "Cannot read properties of null (reading 'sequence')",
  // Cross-origin script with no CORS headers: the browser strips everything, so the
  // event carries no message, no stack and no file. Nothing is recoverable from it.
  'Script error.',
  // Benign browser scheduling notices, not faults. Fired by layout-observing UI
  // (the editor uses ResizeObserver heavily) and safe to drop by universal consensus.
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
];

export const initErrorReporter = (): void => {
  if (initialized || import.meta.env.DEV) return;

  // The dev deploy builds with the same VITE_SENTRY_DSN as production (.gitlab-ci.yml
  // passes $VITE_SENTRY_DSN to both jobs), so its events land in the same project and
  // inflate the same issues — a dev event turned up inside a 489-event production issue
  // during triage. Until dev gets its own Sentry project and DSN, it doesn't report.
  if (SENTRY_ENVIRONMENT !== 'production') return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    // Must match the release name the Sentry Vite plugin uploads source maps under
    // (both come from $CI_COMMIT_SHORT_SHA), otherwise events won't be de-minified.
    release: import.meta.env.VITE_SENTRY_RELEASE,
    environment: SENTRY_ENVIRONMENT,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Only report errors thrown from our own scripts, so externally-hosted third parties
    // never show up as noise. Doesn't cover inline or injected scripts — see
    // THIRD_PARTY_NOISE for those.
    allowUrls: [/editar-pdf\.com/],
    ignoreErrors: THIRD_PARTY_NOISE,
    // Browser extensions inject content scripts under their own scheme.
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari(-web)?-extension:\/\//],
    beforeSend(event, hint) {
      return isBenignAbort(hint?.originalException) ? null : event;
    },
  });
  initialized = true;

  // initErrorReporter runs at module scope in main.tsx, before the React root renders,
  // so anything that throws in here takes the whole app down with it.
  // Sentry is live by this point, so a failure reports itself.
  try {
    trackConnection();
    applyIdentity();
  } catch (err) {
    Sentry.captureException(err, { tags: { area: 'errorReporter' } });
  }
};

export const errorReporter = {
  /** Every visitor has a guest id, signed in or not. Call as early as it's known. */
  identifyGuest(id: string): void {
    guestId = id;
    applyIdentity();
  },

  identifyAccount(account: { id: string; subscriptionType?: string }): void {
    accountId = account.id;
    applyIdentity();
    if (initialized) {
      Sentry.setTag('subscriptionType', account.subscriptionType);
    }
  },

  /** Signed out: drop the account, keep the guest — it's the same browser either way. */
  forgetAccount(): void {
    accountId = undefined;
    applyIdentity();
    if (initialized) {
      Sentry.setTag('subscriptionType', undefined);
    }
  },

  /**
   * Records which document the user is working on. Clears the page-count tag, which
   * isn't known until the PDF parses — a stale count from the previous document would
   * be worse than none.
   */
  setDocumentContext(context: { sizeBytes: number; source: DocumentSource }): void {
    if (!initialized) return;
    Sentry.setTag('fileSizeBucket', fileSizeBucket(context.sizeBytes));
    Sentry.setTag('documentSource', context.source);
    Sentry.setTag('pageCountBucket', undefined);
  },

  setDocumentPageCount(pages: number): void {
    if (!initialized) return;
    Sentry.setTag('pageCountBucket', pageCountBucket(pages));
  },

  report(err: unknown, context?: ReportContext): void {
    if (isBenignAbort(err)) return;
    if (import.meta.env.DEV || !initialized) {
      // eslint-disable-next-line no-console
      console.error('[errorReporter]', err, context ?? '');
      return;
    }
    Sentry.captureException(err, context);
  },

  breadcrumb(crumb: Breadcrumb): void {
    if (import.meta.env.DEV || !initialized) {
      // eslint-disable-next-line no-console
      console.debug(`[breadcrumb:${crumb.category}]`, crumb.message, crumb.data ?? '');
      return;
    }
    Sentry.addBreadcrumb({
      category: crumb.category,
      message: crumb.message,
      level: crumb.level ?? 'info',
      data: crumb.data,
    });
  },
};
